import React, { useState, useEffect, useRef } from 'react'
import Modal from '../shared/Modal'
import StatusBadge from '../shared/StatusBadge'
import { apiService } from '../../services/api'
import { MaintenanceRequest, MaintenanceItem, MaintenanceUpdateCreateDTO } from '../../types/maintenance'
import toast from 'react-hot-toast'
import { MessageCircle, Clock, User, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react'

interface UpdateModalProps {
  isOpen: boolean
  onClose: () => void
  request: MaintenanceRequest
  onUpdate?: (updatedRequest: MaintenanceItem) => void
}

export default function UpdateModal({ isOpen, onClose, request, onUpdate }: UpdateModalProps) {
  const [message, setMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const messageTextareaRef = useRef<HTMLTextAreaElement>(null)

  // Resetar formulário quando modal abrir
  useEffect(() => {
    if (isOpen) {
      setMessage('')
      setError(null)
      // Focar no textarea quando modal abrir
      setTimeout(() => {
        messageTextareaRef.current?.focus()
      }, 100)
    }
  }, [isOpen])

  // Validação de mensagem
  const validateMessage = (text: string): string | null => {
    const trimmedText = text.trim()
    
    if (trimmedText.length === 0) {
      return 'A mensagem é obrigatória'
    }
    
    if (trimmedText.length < 10) {
      return 'A mensagem deve ter pelo menos 10 caracteres'
    }
    
    if (trimmedText.length > 500) {
      return 'A mensagem deve ter no máximo 500 caracteres'
    }
    
    return null
  }

  const validationError = validateMessage(message)
  const canSubmit = !validationError && !isSubmitting && message.trim().length > 0

  // Preview formatado
  const getPreviewText = (): string => {
    const currentUser = JSON.parse(localStorage.getItem('authUser') || '{}')
    const authorName = currentUser.name || 'Utilizador'

    const timestamp = new Date().toLocaleString('pt-PT', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).replace(',', '')

    return `${timestamp} - ${authorName}: ${message.trim()}`
  }

  // Formatar data para exibição
  const formatDate = (date: Date | string): string => {
    const d = typeof date === 'string' ? new Date(date) : date
    return d.toLocaleString('pt-PT', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).replace(',', '')
  }


  // Enviar update
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    const validationError = validateMessage(message)
    if (validationError) {
      setError(validationError)
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      const updateData: MaintenanceUpdateCreateDTO = {
        message: message.trim()
      }

      const updatedRequest = await apiService.postMaintenanceUpdate(request.id, updateData)
      
      // Validar se o request foi retornado corretamente
      if (!updatedRequest?.id) {
        toast.error('Erro ao processar atualização');
        return;
      }
      
      toast.success('Atualização registrada com sucesso!')
      setMessage('')
      
      // Notificar componente pai - map to MaintenanceItem
      if (onUpdate) {
        onUpdate(updatedRequest as unknown as MaintenanceItem);
      }
      
      // Fechar modal após sucesso
      setTimeout(() => {
        onClose()
      }, 1000)
      
    } catch (err) {
      const errObj = err as Record<string, unknown>
      const responseData = errObj?.response as Record<string, unknown> | undefined
      const errorMessage: string = (typeof (responseData?.data as Record<string, unknown>)?.message === 'string'
        ? (responseData?.data as Record<string, unknown>).message as string
        : null) || (typeof errObj?.message === 'string' ? errObj.message as string : null) || 'Erro ao registrar atualização'
      setError(errorMessage)
      toast.error(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="flex items-center">
          <MessageCircle className="h-5 w-5 mr-2 text-brand-primary-500" />
          Adicionar Atualização
        </div>
      }
      size="lg"
      showCloseButton={false}
    >
      <div className="space-y-6">
        {/* Informações da requisição */}
        <div className="bg-brand-surface-50 rounded-lg p-4 border border-brand-neutral-200">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-brand-text-primary">
              Requisição {request.id.slice(-8)}
            </h3>
            <StatusBadge status={request.status} />
          </div>
          <p className="text-sm text-brand-text-secondary mb-1">
            {request.asset?.description || request.equipmentDescription || 'Equipamento não identificado'}
          </p>
          <p className="text-xs text-brand-text-muted">
            Criada em {formatDate(request.requestDate)}
          </p>
        </div>

        {/* Formulário de update */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="update-message" className="block text-sm font-medium text-brand-text-primary mb-2">
              Mensagem de Atualização
            </label>
            <textarea
              ref={messageTextareaRef}
              id="update-message"
              value={message}
              onChange={(e) => {
                setMessage(e.target.value)
                setError(null)
              }}
              disabled={isSubmitting}
              className={`
                w-full rounded-lg border border-brand-neutral-300 bg-white px-3 py-2 text-brand-text-primary
                placeholder:text-brand-text-muted focus:outline-none focus:ring-2 focus:ring-brand-primary-500
                focus:border-brand-primary-500 disabled:opacity-50 disabled:cursor-not-allowed resize-none
                ${validationError ? 'border-amber-300 focus:ring-amber-500 focus:border-amber-500' : ''}
              `}
              rows={4}
              placeholder="Descreva o progresso, observações ou próximos passos..."
              aria-describedby="message-help message-error"
              aria-invalid={!!validationError}
            />
            
            {/* Contador de caracteres e ajuda */}
            <div className="mt-2 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className={`text-xs ${
                  message.length > 500 ? 'text-red-600' : 
                  message.length < 10 ? 'text-brand-text-muted' : 'text-brand-text-secondary'
                }`}>
                  {message.length}/500 caracteres
                </span>
                {message.length > 0 && message.length < 10 && (
                  <AlertCircle className="h-3 w-3 text-amber-500" />
                )}
                {message.length >= 10 && message.length <= 500 && (
                  <CheckCircle2 className="h-3 w-3 text-green-500" />
                )}
              </div>
              <span id="message-help" className="text-xs text-brand-text-muted">
                Mínimo 10 caracteres
              </span>
            </div>

            {/* Erro de validação */}
            {validationError && (
              <div 
                id="message-error"
                className="mt-2 flex items-center space-x-2 text-sm text-red-600"
                role="alert"
                aria-live="polite"
              >
                <AlertCircle className="h-4 w-4" />
                <span>{validationError}</span>
              </div>
            )}

            {/* Erro de API */}
            {error && !validationError && (
              <div 
                className="mt-2 flex items-center space-x-2 text-sm text-red-600"
                role="alert"
                aria-live="polite"
              >
                <AlertCircle className="h-4 w-4" />
                <span>{error}</span>
              </div>
            )}
          </div>

          {/* Preview formatado */}
          {message.trim().length > 0 && (
            <div className="bg-brand-neutral-50 rounded-lg p-4 border border-brand-neutral-200">
              <h4 className="text-sm font-medium text-brand-text-primary mb-2 flex items-center">
                <MessageCircle className="h-4 w-4 mr-2" />
                Preview da Mensagem
              </h4>
              <div className="text-sm text-brand-text-secondary font-mono bg-white rounded p-2 border border-brand-neutral-300">
                {getPreviewText()}
              </div>
              {message.length > 500 && (
                <p className="mt-2 text-xs text-red-600">
                  ⚠️ A mensagem será truncada para 500 caracteres
                </p>
              )}
            </div>
          )}

          {/* Histórico de updates */}
          {request.updates && request.updates.length > 0 && (
            <div className="bg-brand-surface-50 rounded-lg p-4 border border-brand-neutral-200">
              <h4 className="text-sm font-medium text-brand-text-primary mb-3 flex items-center">
                <Clock className="h-4 w-4 mr-2" />
                Histórico de Atualizações ({request.updates.length})
              </h4>
              <div className="space-y-3 max-h-60 overflow-y-auto">
                {request.updates
                  .slice()
                  .map((update, index) => (
                    <div
                      key={update.id}
                      className={`
                        bg-white rounded-lg p-3 border border-brand-neutral-200
                        ${index === 0 ? 'ring-2 ring-brand-primary-100 border-brand-primary-300' : ''}
                      `}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <User className="h-4 w-4 text-brand-text-muted" />
                          <span className="text-sm font-medium text-brand-text-primary">
                            {update.authorName || update.authorId || 'Utilizador'}
                          </span>
                        </div>
                        <span className="text-xs text-brand-text-muted">
                          {formatDate(update.timestamp)}
                        </span>
                      </div>
                      <p className="text-sm text-brand-text-secondary leading-relaxed">
                        {update.message}
                      </p>
                      {index === 0 && (
                        <div className="mt-2 text-xs text-brand-primary-600 font-medium">
                          Mais recente
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Botões de ação */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-brand-neutral-200">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="btn-neutral"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="btn-primary flex items-center space-x-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Enviando...</span>
                </>
              ) : (
                <>
                  <MessageCircle className="h-4 w-4" />
                  <span>Registrar Atualização</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  )
}